<script lang="ts">
import { useRouteMeta } from '@/plugins/useRouteMeta'
import { useBenefitsStore } from '@/stores/benefits'
import { usePlansStore } from '@/stores/plans'
import type { AccordionContent, Plan } from '@/types'
import { type PropType, defineComponent } from 'vue'

/**
 * A button to upgrade to a higher plan.
 */
export default defineComponent({
  props: {
    /**
     * The currently logged in user's highest plan.
     * If user is on monthly plan, this will be the yearly plan for same tier, if exists.
     */
    userPlan: {
      type: Object as PropType<Plan>,
      required: true,
    },
    /**
     * The plan to upgrade to.
     */
    upgradePlan: {
      type: Object as PropType<Plan>,
      required: true,
    },
    /**
     * The type of upgrade to perform.
     */
    upgradeType: {
      type: String as PropType<'upgrade' | 'upsell'>,
      required: false,
    },
  },
  emits: ['close-modal'],
  setup() {
    return {
      plansStore: usePlansStore(),
      routeMeta: useRouteMeta().meta,
      benefitsStore: useBenefitsStore(),
    }
  },
  data() {
    return {
      isLoading: false,
      showConfirmation: false,
    }
  },
  computed: {
    planCost(): number {
      return Math.max(
        this.upgradePlan.discount_cost - this.userPlan.discount_cost,
        0
      )
    },
    upgradePlanFeatures(): Array<AccordionContent> {
      return this.upgradePlan.features.map((f: AccordionContent) => ({
        ...f,
        pre_icon: {
          icon: this.Icons.Lightening,
          class: 'text-tertiary',
        },
      }))
    },
    isOnBenefitsPage(): boolean {
      const { name } = this.$router.currentRoute.value
      return (
        name === 'benefits' ||
        name === 'members-zone-benefits' ||
        name === 'members-zone-benefits-vouchers'
      )
    },
  },
  unmounted() {
    this.showConfirmation = false
  },
  methods: {
    async upgrade() {
      try {
        this.isLoading = true
        const targetPlanCode = this.upgradePlan.code ?? null

        const { success, alreadyOnPlan } = await this.plansStore.upgrade(
          this.upgradePlan,
          this.userPlan,
          this.upgradeType
        )

        if (alreadyOnPlan) {
          this.$alertStore.addSuccessAlert({
            message: `You're already on ${this.upgradePlan.name}.`,
            buttonText: 'Continue',
          })
          this.$emit('close-modal')
          return
        }

        if (success) {
          // Wait until the backend reflects the new plan (covers PSP/webhook delays)
          try {
            await this.$userStore.waitForPlan(targetPlanCode)
          } catch (error) {
            this.$alertStore.addErrorAlert({
              message:
                'Your upgrade is processing. We will refresh your account shortly.',
            })
            return
          }

          const alertPayload = {
            message: 'Subscription successfully upgraded.',
            buttonText: 'Continue',
          }

          if (this.isOnBenefitsPage) {
            this.$emit('close-modal')
            this.$alertStore.addSuccessAlert(alertPayload)
          } else {
            this.$alertStore.addSuccessAlert(alertPayload)
            void this.$router.push({ name: 'members-zone' })
          }
        }
      } catch (e: any) {
        this.$alertStore.addErrorAlert({
          message: e.error?.message ?? e.message,
        })
      } finally {
        this.isLoading = false
      }
    },
  },
})
</script>

<template>
  <AppButton
    :title="`Upgrade to ${upgradePlan.name}`"
    :is-loading="isLoading"
    :icon="Icons.ArrowRight"
    class="w-full font-bold capitalize !h-11 button-primary mx-auto mt-6"
    @click="upgrade"
  >
    {{ `Upgrade to ${upgradePlan.name}` }}
  </AppButton>
</template>

<style lang="scss" scoped></style>
